import type { IAggFunc, IAggFuncParams, IRowNode } from 'ag-grid-community';
import { getAllParentsOfNodeInclusive } from '../utils';

/**
 * Checks if a field is in the grouping hierarchy by traversing up the parent chain.
 * Returns true if the specified field is found anywhere in the hierarchy (current node or any parent).
 */
export function isFieldGroupInHierarchy(rowNode: IRowNode | null | undefined, field: string): boolean {
  if (!rowNode) {
    return false;
  }

  // Traverse up the parent chain (includes the current node)
  const allParents = getAllParentsOfNodeInclusive(rowNode);

  // Check if any node in the hierarchy has the specified field as its grouping field
  return allParents.some(node => node.field === field);
}

/**
 * Creates an aggregation function that only aggregates when a specific field is in the grouping hierarchy.
 * When the field is not in the hierarchy (above the target field level), it returns blank/empty.
 *
 * @param fieldName - The field name that must be present in the hierarchy for aggregation to occur (e.g., 'Asset')
 * @param aggregateValues - Function that determines how to aggregate the values when the field is in the hierarchy
 * @returns An aggFunc compatible with AG Grid column definitions
 */
export function createFieldLimitedAggFunc<T = string>(fieldName: string, aggregateValues: IAggFunc<T>): IAggFunc<T> {
  return (params: IAggFuncParams<T>) => {
    const { rowNode } = params;
    const isTargetLevel = isFieldGroupInHierarchy(rowNode, fieldName);

    if (!isTargetLevel) {
      return '';
    }

    return aggregateValues(params);
  };
}
